import axios from 'axios';
import { getBackendApiBaseUrl } from '@/config/backendApi';
import type {
  MailListFilter,
  MailListFilterCounts,
  MailFolder,
  MailFolderType,
  MailMessage,
  MailMessageSummary,
  MailMessageListResponse,
  MailSearchResult,
  MailLabel,
  MailAccount,
  SendMessagePayload,
  SendIdentity,
} from '../types/mail.types';
import type { AppearancePreferences, NotificationPreferences } from '../store/settingsStore';
import type { AppPreferencesUpdate } from '@/types/appPreferences';

// Dedicated Axios instance for the bounded WeBeMail client endpoints.
const mailApi = axios.create({
  baseURL: getBackendApiBaseUrl('/api/v1/webemail'),
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

const attachmentContextById = new Map<string, { mailboxId: string; messageId: string }>();

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function extractPayload<T>(data: unknown, key: string): T {
  const record = asRecord(data);
  return (record[key] ?? data) as T;
}

// Inject Bearer token from localStorage (set by Keycloak in App.tsx)
mailApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('webelieve_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Include tenant realm header
  const firmId = localStorage.getItem('webelieveSubscriberId');
  if (firmId) {
    config.headers['X-Realm-ID'] = firmId;
  }
  return config;
});

// ── Mailbox Accounts ────────────────────────────────────────────────────────

// Map backend DB row shape to frontend MailAccount interface
function mapMailbox(raw: Record<string, unknown>): MailAccount {
  return {
    id: (raw.mailbox_id as string) || (raw.id as string),
    email: raw.email as string,
    displayName: (raw.display_name as string) || (raw.displayName as string) || '',
    avatarUrl: (raw.avatar_url as string) || undefined,
    isPrimary: !!(raw.is_primary ?? raw.isPrimary ?? false),
    tenantId: (raw.tenant_id as string) || (raw.tenantId as string) || '',
  };
}

export async function fetchMailboxAccounts(): Promise<MailAccount[]> {
  const { data } = await mailApi.get('/mailboxes');
  const rawMailboxes: Record<string, unknown>[] = data.mailboxes ?? data;
  return rawMailboxes.map(mapMailbox);
}

export async function fetchMailbox(mailboxId: string): Promise<MailAccount> {
  const mailboxes = await fetchMailboxAccounts();
  const mailbox = mailboxes.find((item) => item.id === mailboxId);
  if (!mailbox) {
    throw new Error('Mailbox not found');
  }
  return mailbox;
}

export async function getMailboxQuota(mailboxId: string): Promise<{ usedBytes: number; quotaBytes: number }> {
  const { data } = await mailApi.get('/mailboxes');
  const rawMailboxes: Record<string, unknown>[] = data.mailboxes ?? data;
  const mailbox = rawMailboxes.find((item) => (item.id || item.mailbox_id) === mailboxId);
  return {
    usedBytes: Number(mailbox?.usedBytes ?? mailbox?.used_bytes ?? 0),
    quotaBytes: Number(mailbox?.quotaBytes ?? mailbox?.quota_bytes ?? 0),
  };
}

export async function fetchSendIdentities(mailboxId: string): Promise<SendIdentity[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/send-identities`);
  return data.identities ?? [];
}

export async function updateMailboxSettings(
  mailboxId: string,
  settings: Record<string, unknown>,
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/settings`, mapMailboxSettingsPayload(settings));
}

// ── User Preferences (Appearance/Notifications) ────────────────────────────

export interface UserPreferencesPayload {
  appearancePreferences?: Partial<AppearancePreferences>;
  notificationPreferences?: Partial<NotificationPreferences>;
  appPreferences?: AppPreferencesUpdate;
}

function mapMailboxSettingsPayload(settings: Record<string, unknown>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const source = settings || {};
  const mappings: Array<[string, string]> = [
    ['display_name', 'displayName'],
    ['displayName', 'displayName'],
    ['auto_reply_enabled', 'autoReplyEnabled'],
    ['autoReplyEnabled', 'autoReplyEnabled'],
    ['auto_reply_subject', 'autoReplySubject'],
    ['autoReplySubject', 'autoReplySubject'],
    ['auto_reply_body', 'autoReplyBody'],
    ['autoReplyBody', 'autoReplyBody'],
    ['auto_reply_start', 'autoReplyStart'],
    ['autoReplyStart', 'autoReplyStart'],
    ['auto_reply_end', 'autoReplyEnd'],
    ['autoReplyEnd', 'autoReplyEnd'],
    ['forward_to', 'forwardTo'],
    ['forwardTo', 'forwardTo'],
    ['forward_keep_copy', 'forwardKeepCopy'],
    ['forwardKeepCopy', 'forwardKeepCopy'],
    ['signature_html', 'signatureHtml'],
    ['signatureHtml', 'signatureHtml'],
    ['signature_plain', 'signaturePlain'],
    ['signaturePlain', 'signaturePlain'],
    ['settings', 'settings'],
  ];

  for (const [from, to] of mappings) {
    if (Object.prototype.hasOwnProperty.call(source, from)) {
      payload[to] = source[from];
    }
  }

  return payload;
}

export async function fetchUserPreferences(): Promise<UserPreferencesPayload | null> {
  try {
    const { data } = await mailApi.get('/preferences');
    return (data?.preferences ?? data) ?? null;
  } catch {
    return null;
  }
}

export async function updateUserPreferences(prefs: UserPreferencesPayload): Promise<void> {
  await mailApi.patch('/preferences', prefs);
}

// ── Folders ─────────────────────────────────────────────────────────────────

// Map backend special_use string to frontend MailFolderType
function mapSpecialUseToType(specialUse: string | null): MailFolderType {
  const map: Record<string, MailFolderType> = {
    '\\Inbox': 'inbox',
    '\\Sent': 'sent',
    '\\Drafts': 'drafts',
    '\\Trash': 'deleted',
    '\\Junk': 'junk',
    '\\Archive': 'archive',
    '\\Snoozed': 'snoozed',
    '\\Flagged': 'flagged',
  };
  return (specialUse && map[specialUse]) || 'custom';
}

// Map backend DB row shape to frontend MailFolder interface
function mapFolder(raw: Record<string, unknown>): MailFolder {
  return {
    id: (raw.folder_id as string) || (raw.id as string),
    mailboxId: (raw.mailbox_id as string) || (raw.mailboxId as string),
    name: raw.name as string,
    path: raw.path as string | undefined,
    type: mapSpecialUseToType((raw.special_use as string | null) ?? (raw.specialUse as string | null)),
    parentId: (raw.parent_folder_id as string) || (raw.parentId as string) || null,
    unreadCount: Number(raw.unread_count ?? raw.unreadCount ?? 0),
    totalCount: Number(raw.message_count ?? raw.messageCount ?? 0),
    children: [],
    specialUse: (raw.special_use as string | undefined) || (raw.specialUse as string | undefined),
    sortOrder: Number(raw.sort_order ?? raw.sortOrder ?? 0),
    iconColor: (raw.icon_color as string) || (raw.iconColor as string) || undefined,
    linkedMatterId: (raw.linked_matter_id as string) || undefined,
    linkedClientId: (raw.linked_client_id as string) || undefined,
    autoFileEnabled: (raw.auto_file_enabled as boolean) || false,
  };
}

/**
 * Build a nested folder tree from the flat list of folders returned by the
 * API. Migrated folders carry their hierarchy in the `path` column
 * (e.g. `INBOX/Clients/Acme`), and `parent_folder_id` is often NULL on
 * migrated rows — so we anchor the tree on `path` rather than `parentId`.
 *
 * Algorithm:
 *   1. Sort by path so parents come before children (`INBOX` before
 *      `INBOX/Clients` before `INBOX/Clients/Acme`).
 *   2. For each folder, find its parent by stripping the last path segment
 *      and matching against folders with that path.
 *   3. Roots (folders whose path has no `/` AND that exist in the input)
 *      are collected for the top level. Children are pushed into their
 *      parent's `children[]`. The leaf `name` is shown for nested folders
 *      so the sidebar reads `Acme` rather than `INBOX/Clients/Acme`.
 *
 * If a folder's stated parent path doesn't exist in the input (orphan), the
 * folder is treated as a root so it's still visible — never silently lost.
 */
function buildFolderTree(folders: MailFolder[], rawByPath: Map<string, string>): MailFolder[] {
  // Sort shortest path first so parents are seen first; preserves explicit sort_order within a depth.
  const sorted = [...folders].sort((a, b) => {
    const pa = rawByPath.get(a.id) ?? a.name;
    const pb = rawByPath.get(b.id) ?? b.name;
    const da = (pa.match(/\//g) ?? []).length;
    const db = (pb.match(/\//g) ?? []).length;
    if (da !== db) return da - db;
    return (a.sortOrder - b.sortOrder) || pa.localeCompare(pb);
  });

  const byPath = new Map<string, MailFolder>();
  const roots: MailFolder[] = [];

  for (const f of sorted) {
    f.children = [];
    const path = rawByPath.get(f.id);
    if (!path || !path.includes('/')) {
      byPath.set(path ?? f.name, f);
      roots.push(f);
      continue;
    }
    // Show the leaf name in the sidebar, keep the full path for create/move calls.
    const segments = path.split('/');
    const leaf = segments[segments.length - 1];
    if (leaf && leaf.length > 0) f.name = leaf;
    const parentPath = segments.slice(0, -1).join('/');
    const parent = byPath.get(parentPath);
    if (parent) {
      parent.children.push(f);
    } else {
      // Orphan (parent missing from response) — surface as root rather than hide.
      roots.push(f);
    }
    byPath.set(path, f);
  }
  return roots;
}

export async function fetchFolders(mailboxId: string, tab?: string): Promise<MailFolder[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/folders`, {
    params: tab ? { tab } : undefined,
  });
  const rawFolders: Record<string, unknown>[] = data.folders ?? data;
  const mapped = rawFolders.map(mapFolder);
  // Build a folder_id → path lookup so the tree-builder can group on `path`.
  const rawByPath = new Map<string, string>();
  for (const r of rawFolders) {
    const id = (r.folder_id as string) || (r.id as string);
    const path = (r.path as string) ?? '';
    if (id) rawByPath.set(id, path);
  }
  return buildFolderTree(mapped, rawByPath);
}

export async function updateFolder(
  mailboxId: string,
  folderId: string,
  updates: { name?: string; icon_color?: string | null; auto_file_enabled?: boolean },
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/folders/${folderId}`, {
    name: updates.name,
    iconColor: updates.icon_color,
  });
}

export async function createFolder(
  mailboxId: string,
  name: string,
  options?: {
    parentPath?: string;
    parent_folder_id?: string;
    linked_matter_id?: string;
    linked_client_id?: string;
  },
): Promise<MailFolder> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders`, {
    name,
    parentId: options?.parent_folder_id,
  });
  return mapFolder(extractPayload<Record<string, unknown>>(data, 'folder'));
}

export async function deleteFolder(mailboxId: string, folderId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/folders/${folderId}`);
}

export async function markAllAsRead(mailboxId: string, folderId: string): Promise<{ affected: number }> {
  void mailboxId;
  void folderId;
  throw new Error('Mark all as read is not yet available in WeBeMail.');
}

export async function emptyFolder(mailboxId: string, folderId: string): Promise<{ affected: number }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/empty`);
  return { affected: Number(data?.affected ?? 0) };
}

export async function previewFolderRelatedEmails(
  mailboxId: string,
  folderId: string,
): Promise<MailSearchResult & { sourceRuleCount: number }> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/messages`, {
    params: { folderId, limit: 50 },
  });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: rawMessages.length,
    query: `folder:${folderId}`,
    sourceRuleCount: 0,
  };
}

// ── Messages ────────────────────────────────────────────────────────────────

// Map backend EdgeMailMessage entity (snake_case) to frontend MailMessageSummary
function mapMessageSummary(raw: Record<string, unknown>): MailMessageSummary {
  return {
    id: (raw.message_id as string) || (raw.id as string),
    mailboxId: (raw.mailbox_id as string) || (raw.mailboxId as string) || '',
    folderId: (raw.folder_id as string) || (raw.folderId as string) || '',
    from: {
      name: (raw.from_name as string) || (raw.fromName as string) || '',
      email: (raw.from_address as string) || (raw.fromAddress as string) || '',
    },
    subject: (raw.subject as string) || '',
    snippet: (raw.preview as string) || (raw.snippet as string) || '',
    date: (raw.received_at as string) || (raw.receivedAt as string) || (raw.date_header as string) || (raw.dateHeader as string) || (raw.date as string) || '',
    isRead: !!(raw.is_seen ?? raw.isSeen ?? raw.isRead ?? false),
    isFlagged: !!(raw.is_flagged ?? raw.isFlagged ?? false),
    isStarred: !!(raw.is_flagged ?? raw.isStarred ?? false),
    isDraft: !!(raw.is_draft ?? raw.isDraft ?? false),
    hasAttachments: !!(raw.has_attachments ?? raw.hasAttachments ?? false),
    isPrivileged: !!(raw.is_privileged ?? raw.isPrivileged ?? false),
    isOnHold: !!(raw.is_on_hold ?? raw.isOnHold ?? false),
    threadId: (raw.thread_id as string) || (raw.threadId as string) || undefined,
    threadCount: (raw.thread_count as number) || (raw.threadCount as number) || undefined,
  };
}

export async function fetchMessages(
  mailboxId: string,
  folderId: string,
  params: {
    tab?: string;
    cursor?: string;
    limit?: number;
    view?: 'flat' | 'threaded';
    filters?: MailListFilter[];
    sender?: string;
    domain?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {},
): Promise<MailMessageListResponse> {
  const offset = params.cursor && /^\d+$/.test(params.cursor) ? Number(params.cursor) : undefined;
  const limit = params.limit ?? 50;
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/messages`, {
    params: {
      folderId,
      tab: params.tab,
      limit,
      offset,
    },
  });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || rawMessages.length,
    nextCursor: rawMessages.length === limit ? String((offset ?? 0) + rawMessages.length) : undefined,
    filterCounts: (data.filterCounts ?? data.filter_counts) as MailListFilterCounts | undefined,
  };
}

// Map backend EdgeMailMessage entity to frontend MailMessage (full detail)
function mapMessageFull(raw: Record<string, unknown>): MailMessage {
  const mapAddr = (a: any) => ({ name: a?.name || '', email: a?.email || a?.address || '' });
  const toAddrs = (raw.to_addresses ?? raw.toAddresses ?? []) as any[];
  const ccAddrs = (raw.cc_addresses ?? raw.ccAddresses ?? []) as any[];
  const messageId = (raw.message_id as string) || (raw.id as string);
  const mailboxId = (raw.mailbox_id as string) || (raw.mailboxId as string) || '';
  const bodyText = (raw.body_plain as string) || (raw.bodyText as string) || (raw.body_text as string) || (raw.body as string) || '';
  const bodyContentType = String(raw.bodyContentType ?? raw.body_content_type ?? '').toLowerCase();
  const mappedAttachments = ((raw.attachments ?? []) as any[]).map((a) => {
    const attachmentId = a.attachment_id || a.id || '';
    if (attachmentId && mailboxId && messageId) {
      attachmentContextById.set(attachmentId, { mailboxId, messageId });
    }
    return {
      id: attachmentId,
      filename: a.filename || a.file_name || '',
      mimeType: a.mime_type || a.mimeType || a.content_type || a.contentType || '',
      size: a.size || a.size_bytes || a.sizeBytes || 0,
      contentId: a.content_id || a.contentId || undefined,
    };
  });
  const rawMatterLinks = ((raw.matter_links ?? raw.matterLinks) as any[] | undefined)?.length
    ? (raw.matter_links ?? raw.matterLinks) as any[]
    : (raw.linked_matter_id || raw.linkedMatterId)
      ? [{ id: (raw.linked_matter_id || raw.linkedMatterId) as string, matterId: (raw.linked_matter_id || raw.linkedMatterId) as string, matterName: '', matterNumber: '' }]
      : [];
  const matterLinks = rawMatterLinks.map((link) => ({
    id: link.id || link.matter_id || link.matterId || '',
    matterId: link.matterId || link.matter_id || link.id || '',
    matterName: link.matterName || link.matter_name || link.description || '',
    matterNumber: link.matterNumber || link.matter_number || '',
    clientId: link.clientId || link.client_id || undefined,
    clientName: link.clientName || link.client_name || undefined,
    clientCode: link.clientCode || link.client_code || undefined,
    clientNumber: link.clientNumber || link.client_number || undefined,
  }));
  const rawClientLinks = ((raw.client_links ?? raw.clientLinks) as any[] | undefined)?.length
    ? (raw.client_links ?? raw.clientLinks) as any[]
    : (raw.linked_client_id || raw.linkedClientId)
      ? [{ id: (raw.linked_client_id || raw.linkedClientId) as string, clientId: (raw.linked_client_id || raw.linkedClientId) as string, clientName: '' }]
      : [];
  const clientLinks = rawClientLinks.map((link) => ({
    id: link.id || link.client_id || link.clientId || '',
    clientId: link.clientId || link.client_id || link.id || '',
    clientName: link.clientName || link.client_name || '',
    clientCode: link.clientCode || link.client_code || undefined,
    clientNumber: link.clientNumber || link.client_number || undefined,
  }));
  if (clientLinks.length === 0) {
    const linkedMatterClient = matterLinks.find((link) => link.clientId);
    if (linkedMatterClient?.clientId) {
      clientLinks.push({
        id: linkedMatterClient.clientId,
        clientId: linkedMatterClient.clientId,
        clientName: linkedMatterClient.clientName || '',
        clientCode: linkedMatterClient.clientCode,
        clientNumber: linkedMatterClient.clientNumber,
      });
    }
  }

  return {
    id: messageId,
    mailboxId,
    folderId: (raw.folder_id as string) || (raw.folderId as string) || '',
    from: {
      name: (raw.from_name as string) || (raw.fromName as string) || '',
      email: (raw.from_address as string) || (raw.fromAddress as string) || '',
    },
    to: toAddrs.map(mapAddr),
    cc: ccAddrs.map(mapAddr),
    bcc: ((raw.bcc_addresses ?? raw.bccAddresses ?? []) as any[]).map(mapAddr),
    subject: (raw.subject as string) || '',
    snippet: (raw.preview as string) || (raw.snippet as string) || '',
    bodyHtml: (raw.body_html as string) || (raw.bodyHtml as string) || (bodyContentType.includes('html') ? bodyText : ''),
    bodyText,
    date: (raw.received_at as string) || (raw.receivedAt as string) || (raw.date_header as string) || (raw.dateHeader as string) || (raw.date as string) || '',
    isRead: !!(raw.is_seen ?? raw.isSeen ?? raw.isRead ?? false),
    isFlagged: !!(raw.is_flagged ?? raw.isFlagged ?? false),
    isStarred: !!(raw.is_flagged ?? raw.isStarred ?? false),
    isDraft: !!(raw.is_draft ?? raw.isDraft ?? false),
    hasAttachments: !!(raw.has_attachments ?? raw.hasAttachments ?? false),
    isPrivileged: !!(raw.is_privileged ?? raw.isPrivileged ?? false),
    isOnHold: !!(raw.is_on_hold ?? raw.isOnHold ?? false),
    attachments: mappedAttachments,
    labels: asArray<string>(raw.labels).map((l) => ({ id: l, name: l, color: '' })),
    matterLinks,
    clientLinks,
    snoozedUntil: (raw.snoozed_until as string | null) ?? (raw.snoozedUntil as string | null) ?? null,
  };
}

export async function fetchMessage(mailboxId: string, messageId: string): Promise<MailMessage> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/messages/${messageId}`);
  // Backend returns { message: { ...fields, body: "<html>" }, attachments: [...] }
  const raw = data.message ?? data;
  // Merge top-level attachments into the raw message object for mapMessageFull
  if (data.attachments && !raw.attachments) {
    raw.attachments = data.attachments;
  }
  return mapMessageFull(raw);
}

/**
 * Download the raw RFC 822 source as a .eml file. The backend streams the
 * exact bytes from Dovecot, preserving headers and signatures — which is
 * what "Save as .eml" needs to be legally meaningful. We fetch as a blob
 * (so the bearer token header is honored) and trigger a click on a
 * temporary <a> to drive the browser's download UI; this avoids the
 * `window.open` path which can't attach auth headers.
 */
export async function downloadMessageEml(
  mailboxId: string,
  messageId: string,
  suggestedFilename?: string,
): Promise<void> {
  const response = await mailApi.get(
    `/mailboxes/${mailboxId}/messages/${messageId}/raw`,
    { responseType: 'blob', timeout: 60000 },
  );

  // Prefer the filename the server suggested via Content-Disposition; it
  // already includes the subject and a filesystem-safe encoding. Fall back
  // to the caller-supplied suggestion, then to a generic name.
  const disposition = response.headers?.['content-disposition'] as string | undefined;
  const filename =
    parseContentDispositionFilename(disposition) ||
    suggestedFilename ||
    `${messageId}.eml`;

  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'message/rfc822' });

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Delay revoke so Safari can still start the download from the URL
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function parseContentDispositionFilename(header?: string): string | null {
  if (!header) return null;
  // Prefer the RFC 5987 UTF-8 form; fall back to plain filename.
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  return asciiMatch ? asciiMatch[1] : null;
}

export async function sendMessage(
  mailboxId: string,
  payload: SendMessagePayload,
): Promise<{ messageId: string; queueId?: string; status?: 'queued' | 'sent' }> {
  // Map the compose payload to the bounded WeBeMail send contract.
  const backendPayload = {
    to: payload.to,
    cc: payload.cc,
    bcc: payload.bcc,
    subject: payload.subject,
    bodyHtml: payload.bodyHtml,
    bodyPlain: payload.bodyText || '',
    draftId: payload.draftId,
    sendIdentityId: payload.sendIdentityId,
  };
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/send`, backendPayload);
  const send = data.send ?? data;
  const message = send.message ?? {};
  return {
    messageId: message.id || data.message_id || data.messageId || send.messageIdHeader,
    queueId: send.outboundSendId || send.smtpMessageId || data.queue_id || data.queueId,
    status: send.status,
  };
}

export interface OutboundSendStatus {
  outboundSendId: string;
  status: 'queued' | 'sending' | 'sent' | 'failed';
  attempts: number;
  lastError: string | null;
  smtpMessageId: string | null;
  accepted: string[];
  rejected: string[];
}

export async function fetchOutboundSendStatus(mailboxId: string, outboundSendId: string): Promise<OutboundSendStatus> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/outbound/${outboundSendId}`);
  return data.outbound ?? data;
}

export async function markAsRead(
  mailboxId: string,
  messageId: string,
  isRead: boolean,
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/read`, { isSeen: isRead });
}

export async function toggleFlag(
  mailboxId: string,
  messageId: string,
  isFlagged: boolean,
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/flag`, { isFlagged });
}

export async function moveMessage(
  mailboxId: string,
  messageId: string,
  targetFolderId: string,
): Promise<MoveMessageResponse> {
  const { data } = await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/move`, { targetFolderId });
  return { success: Boolean(data.message) };
}

export interface SenderAutomationSuggestion {
  automationType: 'sender' | 'conversation';
  senderEmail: string;
  senderName?: string | null;
  counterpartEmail?: string | null;
  counterpartName?: string | null;
  sourceThreadId?: string | null;
  targetFolderId: string;
  targetFolderName: string;
  targetFolderPath: string;
  matchingExistingCount: number;
  autoFileRuleExists: boolean;
}

export interface MoveMessageResponse {
  success: boolean;
  senderAutomation?: SenderAutomationSuggestion | null;
}

export interface SenderAutomationResult {
  success: boolean;
  movedExisting: number;
  autoFileRuleCreated: boolean;
  autoFileRuleExists: boolean;
  suggestion?: SenderAutomationSuggestion | null;
}

export async function organizeSenderMessages(
  mailboxId: string,
  messageId: string,
  targetFolderId: string,
  options: { moveExisting: boolean; createAutoFileRule: boolean },
): Promise<SenderAutomationResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/sender-automation`, {
    target_folder_id: targetFolderId,
    move_existing: options.moveExisting,
    create_auto_file_rule: options.createAutoFileRule,
  });
  return data;
}

export type EmailRuleConditionKind = 'from' | 'to' | 'cc' | 'bcc' | 'participant' | 'recipient' | 'subject' | 'domain';
export type EmailRuleScope = 'existing' | 'future' | 'both';

export interface EmailRuleCandidate {
  id: string;
  kind: EmailRuleConditionKind;
  label: string;
  value: string;
  displayValue: string;
  isOwnedAddress?: boolean;
  warning?: string;
}

export interface EmailRuleDefinition {
  conditionKind: EmailRuleConditionKind;
  conditionValue: string;
  targetFolderId: string;
  scope: EmailRuleScope;
}

export interface EmailRulePreviewMessage {
  id: string;
  from: string | null;
  subject: string | null;
  folderId: string;
  receivedAt: string | null;
}

export interface EmailRulePreviewResult {
  matchCount: number;
  samples: EmailRulePreviewMessage[];
}

export interface EmailRuleApplyResult extends EmailRulePreviewResult {
  success: boolean;
  movedExisting: number;
  ruleCreated: boolean;
}

export interface EmailRuleRerunResult {
  success: boolean;
  ruleId: string;
  movedExisting: number;
  targetFolderId: string;
  targetFolderPath: string;
  remainingOutsideTargetCount: number;
}

export interface ManagedEmailRuleCondition {
  field: string;
  operator: string;
  value: string;
  headerName?: string;
}

export interface ManagedEmailRuleAction {
  type: string;
  folderId?: string;
  folder?: string;
  labels?: string[];
  forwardTo?: string;
  forwardKeepCopy?: boolean;
  rejectMessage?: string;
}

export interface ManagedEmailRule {
  ruleId: string;
  mailboxId: string;
  name: string;
  priority: number;
  conditions: ManagedEmailRuleCondition[];
  conditionMatch: 'all' | 'any';
  actions: ManagedEmailRuleAction[];
  isActive: boolean;
  isAiEnhanced: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  targetFolderPath: string | null;
  targetFolderId: string | null;
  matchCount: number | null;
  lastMatchAt: string | null;
  existingOutsideTargetCount: number | null;
}

export interface EmailRuleManagementSuggestion {
  id: string;
  type: 'duplicate' | 'stale' | 'backlog' | 'ai_enhanced';
  severity: 'info' | 'warning';
  title: string;
  description: string;
  ruleIds: string[];
}

export interface EmailRuleManagementResult {
  rules: ManagedEmailRule[];
  suggestions: EmailRuleManagementSuggestion[];
}

export async function fetchRuleDraft(
  mailboxId: string,
  messageId: string,
): Promise<{ candidates: EmailRuleCandidate[] }> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/messages/${messageId}/rule-draft`);
  return data;
}

export async function listEmailRules(mailboxId: string): Promise<EmailRuleManagementResult> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/rules`);
  return data;
}

export async function updateEmailRule(
  mailboxId: string,
  ruleId: string,
  patch: { name?: string; priority?: number; isActive?: boolean },
): Promise<ManagedEmailRule> {
  const { data } = await mailApi.patch(`/mailboxes/${mailboxId}/rules/${ruleId}`, {
    name: patch.name,
    priority: patch.priority,
    is_active: patch.isActive,
  });
  return data.rule;
}

export async function deleteEmailRule(mailboxId: string, ruleId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/rules/${ruleId}`);
}

export async function rerunEmailRule(mailboxId: string, ruleId: string): Promise<EmailRuleRerunResult> {
  const { data } = await mailApi.post<EmailRuleRerunResult>(`/mailboxes/${mailboxId}/rules/${ruleId}/rerun`);
  return data;
}

export async function previewEmailRule(
  mailboxId: string,
  rule: EmailRuleDefinition,
): Promise<EmailRulePreviewResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/rules/preview`, {
    condition_kind: rule.conditionKind,
    condition_value: rule.conditionValue,
    target_folder_id: rule.targetFolderId,
    scope: rule.scope,
  });
  return data;
}

export async function applyEmailRule(
  mailboxId: string,
  rule: EmailRuleDefinition,
): Promise<EmailRuleApplyResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/rules`, {
    condition_kind: rule.conditionKind,
    condition_value: rule.conditionValue,
    target_folder_id: rule.targetFolderId,
    scope: rule.scope,
  });
  return data;
}

export async function reclassifyMessage(
  mailboxId: string,
  messageId: string,
  category: 'focused' | 'other',
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/category`, { category });
}

export async function deleteMessage(mailboxId: string, messageId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/messages/${messageId}`);
}

export async function bulkMoveMessages(
  mailboxId: string,
  messageIds: string[],
  targetFolderId: string,
): Promise<{ moved: number; failed: number; total: number }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/bulk-move`, {
    message_ids: messageIds,
    target_folder_id: targetFolderId,
  });
  return data;
}

export interface FolderMatterLinkRepairPreview {
  folderId: string;
  folderPath: string;
  linkedMatterId: string;
  totalMessages: number;
  alreadyLinkedCount: number;
  unlinkedCount: number;
  conflictingMatterCount: number;
  eligibleCount: number;
  samples: Array<{
    messageId: string;
    subject: string | null;
    from: string | null;
    folderPath: string | null;
    receivedAt: string | null;
    linkedMatterId: string | null;
  }>;
}

export interface FolderMatterLinkRepairResult extends FolderMatterLinkRepairPreview {
  linkedCount: number;
  skippedConflictCount: number;
}

export async function previewFolderMatterLinkRepair(
  mailboxId: string,
  folderId: string,
): Promise<FolderMatterLinkRepairPreview> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/link-messages-to-matter/preview`);
  return data;
}

export async function applyFolderMatterLinkRepair(
  mailboxId: string,
  folderId: string,
  options?: { overrideConflicts?: boolean },
): Promise<FolderMatterLinkRepairResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/folders/${folderId}/link-messages-to-matter/apply`, {
    override_conflicts: options?.overrideConflicts === true,
  });
  return data;
}

export async function linkMessageToMatter(
  mailboxId: string,
  messageId: string,
  matterId: string,
): Promise<{ linkedMatterId: string; movedToFolderId?: string | null; movedToFolderPath?: string | null }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/link-matter`, { matter_id: matterId });
  return {
    linkedMatterId: data.linked_matter_id,
    movedToFolderId: data.moved_to_folder_id,
    movedToFolderPath: data.moved_to_folder_path,
  };
}

export async function linkMessageToClient(
  mailboxId: string,
  messageId: string,
  clientId: string,
): Promise<void> {
  await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/link-client`, { client_id: clientId });
}

// ── Threads ─────────────────────────────────────────────────────────────────

export async function fetchThreadMessages(
  mailboxId: string,
  threadId: string,
): Promise<MailMessageSummary[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/threads/${threadId}/messages`);
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return rawMessages.map(mapMessageSummary);
}

// ── Flagged Messages ────────────────────────────────────────────────────────

export async function fetchFlaggedMessages(
  mailboxId: string,
  options?: { page?: number; limit?: number },
): Promise<{ messages: MailMessageSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.page) params.set('page', String(options.page));
  if (options?.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/flagged${qs ? `?${qs}` : ''}`);
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return { messages: rawMessages.map(mapMessageSummary), total: data.total ?? 0 };
}

// ── Drafts ──────────────────────────────────────────────────────────────────

type DraftPayload = {
  to?: { name?: string; email: string }[];
  cc?: { name?: string; email: string }[];
  bcc?: { name?: string; email: string }[];
  subject?: string;
  bodyHtml?: string;
  body_html?: string;
  bodyPlain?: string;
  body_plain?: string;
  inReplyTo?: string;
  in_reply_to?: string;
  matterId?: string;
  matter_id?: string;
  clientId?: string;
  client_id?: string;
  linkedMatterId?: string;
  linkedClientId?: string;
  isPrivileged?: boolean;
  is_privileged?: boolean;
};

function mapDraftPayload(draft: DraftPayload): Record<string, unknown> {
  return {
    to: draft.to ?? [],
    cc: draft.cc ?? [],
    bcc: draft.bcc ?? [],
    subject: draft.subject ?? '',
    bodyHtml: draft.bodyHtml ?? draft.body_html ?? '',
    bodyPlain: draft.bodyPlain ?? draft.body_plain ?? '',
    inReplyTo: draft.inReplyTo ?? draft.in_reply_to,
    linkedMatterId: draft.linkedMatterId ?? draft.matterId ?? draft.matter_id,
    linkedClientId: draft.linkedClientId ?? draft.clientId ?? draft.client_id,
    isPrivileged: draft.isPrivileged ?? draft.is_privileged,
  };
}

export async function saveDraft(
  mailboxId: string,
  draft: DraftPayload,
): Promise<{ draftId: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/drafts`, mapDraftPayload(draft));
  return { draftId: data.draftId || data.draft_id || data.draft?.id };
}

export async function updateDraft(
  mailboxId: string,
  draftId: string,
  draft: DraftPayload,
): Promise<{ draftId: string }> {
  const { data } = await mailApi.put(`/mailboxes/${mailboxId}/drafts/${draftId}`, mapDraftPayload(draft));
  return { draftId: data.draftId || data.draft_id || data.draft?.id || draftId };
}

export async function deleteDraft(
  mailboxId: string,
  draftId: string,
): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/drafts/${draftId}`);
}

// ── Privilege ────────────────────────────────────────────────────────────────

export async function togglePrivilege(
  mailboxId: string,
  messageId: string,
  isPrivileged: boolean,
  privilegeType: 'attorney-client' | 'work-product' = 'attorney-client',
): Promise<void> {
  await mailApi.patch(`/mailboxes/${mailboxId}/messages/${messageId}/privilege`, {
    is_privileged: isPrivileged,
    privilege_type: isPrivileged ? privilegeType : null,
  });
}

// ── Labels ──────────────────────────────────────────────────────────────────

function mapLabel(raw: Record<string, unknown>): MailLabel {
  return {
    id: (raw.label_id as string) || (raw.id as string),
    name: (raw.name as string) || '',
    color: (raw.color as string) || '',
  };
}

export async function fetchLabels(mailboxId: string): Promise<MailLabel[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/labels`);
  return (data.labels ?? data).map(mapLabel);
}

export async function createLabel(
  mailboxId: string,
  name: string,
  color: string,
): Promise<MailLabel> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/labels`, { name, color });
  return mapLabel(extractPayload<Record<string, unknown>>(data, 'label'));
}

export async function updateLabel(
  mailboxId: string,
  labelId: string,
  updates: Partial<{ name: string; color: string }>,
): Promise<MailLabel> {
  const { data } = await mailApi.patch(`/mailboxes/${mailboxId}/labels/${labelId}`, updates);
  return mapLabel(extractPayload<Record<string, unknown>>(data, 'label'));
}

export async function deleteLabel(mailboxId: string, labelId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/labels/${labelId}`);
}

export async function applyLabel(
  mailboxId: string,
  messageId: string,
  labelId: string,
): Promise<void> {
  await mailApi.post(`/mailboxes/${mailboxId}/messages/${messageId}/labels/${labelId}`);
}

export async function removeLabel(
  mailboxId: string,
  messageId: string,
  labelId: string,
): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/messages/${messageId}/labels/${labelId}`);
}

// ── Search ──────────────────────────────────────────────────────────────────

export async function searchMessages(
  mailboxId: string,
  query: string,
): Promise<MailSearchResult> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/search`, { params: { q: query } });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || 0,
    query: (data.query as string) || query,
  };
}

export async function searchCrossMailbox(query: string): Promise<MailSearchResult> {
  const { data } = await mailApi.get('/search', { params: { q: query } });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || 0,
    query: (data.query as string) || query,
  };
}

export async function searchLinkedMessages(
  mailboxId: string,
  filters: {
    linked_matter_id?: string;
    linked_client_id?: string;
    exclude_special_use?: string[];
  },
): Promise<MailSearchResult> {
  const { exclude_special_use, ...rest } = filters;
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/search`, {
    params: {
      q: '',
      ...rest,
      ...(exclude_special_use?.length ? { exclude_special_use: exclude_special_use.join(',') } : {}),
      limit: 500,
    },
  });
  const rawMessages: Record<string, unknown>[] = data.messages ?? [];
  return {
    messages: rawMessages.map(mapMessageSummary),
    totalCount: (data.total as number) || (data.totalCount as number) || 0,
    query: '',
  };
}

// ── Attachments ─────────────────────────────────────────────────────────────

export interface AttachmentRecord {
  attachment_id: string;
  filename: string;
  content_type?: string;
  size_bytes?: number;
  content_id?: string;
  is_inline?: boolean;
}

export interface AttachmentPreviewResponse {
  status: 'ready' | 'processing' | 'unsupported' | 'failed';
  previewType?: 'pdf' | 'image' | 'text';
  url?: string;
  contentType?: string;
  filename?: string;
  message?: string;
}

export async function createAttachmentUploadUrl(
  mailboxId: string,
  payload: {
    draftId: string;
    filename: string;
    contentType: string;
    sizeBytes: number;
    contentId?: string;
    isInline?: boolean;
  },
): Promise<{ attachment: AttachmentRecord; upload_url: string; expires_at: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/upload-url`, {
    draft_id: payload.draftId,
    filename: payload.filename,
    content_type: payload.contentType,
    size_bytes: payload.sizeBytes,
    content_id: payload.contentId,
    is_inline: payload.isInline,
  });
  return data;
}

export async function finalizeAttachmentUpload(
  mailboxId: string,
  attachmentId: string,
): Promise<{ attachment: AttachmentRecord; checksum_sha256: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/finalize`, {
    attachment_id: attachmentId,
  });
  return data;
}

export async function createDmsAttachment(
  mailboxId: string,
  payload: {
    draftId: string;
    documentId: string;
    documentVersionId: string;
  },
): Promise<{ attachment: AttachmentRecord }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/dms`, {
    draft_id: payload.draftId,
    document_id: payload.documentId,
    document_version_id: payload.documentVersionId,
  });
  return data;
}

export async function cloneAttachmentsToDraft(
  mailboxId: string,
  payload: {
    draftId: string;
    attachmentIds: string[];
  },
): Promise<{ attachments: AttachmentRecord[] }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/attachments/clone`, {
    draft_id: payload.draftId,
    attachment_ids: payload.attachmentIds,
  });
  return data;
}

export async function deleteAttachment(attachmentId: string): Promise<void> {
  await mailApi.delete(`/attachments/${attachmentId}`);
}

export async function getAttachmentDownloadUrl(attachmentId: string): Promise<string> {
  const context = attachmentContextById.get(attachmentId);
  if (!context) {
    throw new Error('Attachment message context is required for WeBeMail downloads.');
  }
  return `/api/v1/webemail/mailboxes/${context.mailboxId}/messages/${context.messageId}/attachments/${attachmentId}/download`;
}

export interface AttachmentDmsImportResult {
  documentId: string;
  versionId: string;
  title: string;
  clientId?: string | null;
  matterId?: string | null;
  duplicate?: {
    isDuplicate: boolean;
    existingDocumentId?: string;
    existingVersionId?: string;
  };
}

export async function importAttachmentToDms(
  attachmentId: string,
  payload: {
    messageId?: string;
    mailboxId?: string;
    clientId?: string;
    matterId?: string;
    title?: string;
    description?: string;
  } = {},
): Promise<AttachmentDmsImportResult> {
  const { data } = await mailApi.post(`/attachments/${attachmentId}/dms-import`, {
    message_id: payload.messageId,
    mailbox_id: payload.mailboxId,
    client_id: payload.clientId,
    matter_id: payload.matterId,
    title: payload.title,
    description: payload.description,
  }, { timeout: 120000 });
  return data;
}

export async function downloadAttachmentBlob(attachmentId: string): Promise<Blob> {
  const context = attachmentContextById.get(attachmentId);
  if (!context) {
    throw new Error('Attachment message context is required for WeBeMail downloads.');
  }
  const { data } = await mailApi.get(
    `/mailboxes/${context.mailboxId}/messages/${context.messageId}/attachments/${attachmentId}/download`,
    { responseType: 'blob', timeout: 60000 },
  );
  return data as Blob;
}

export function getAttachmentDirectUrl(attachmentId: string): string {
  const context = attachmentContextById.get(attachmentId);
  if (!context) {
    return '';
  }
  return `/api/v1/webemail/mailboxes/${context.mailboxId}/messages/${context.messageId}/attachments/${attachmentId}/download`;
}

export async function requestAttachmentPreview(attachmentId: string): Promise<AttachmentPreviewResponse> {
  const { data } = await mailApi.post(`/attachments/${attachmentId}/preview`);
  return data;
}

export async function fetchAttachmentPreviewFile<T = ArrayBuffer>(
  attachmentId: string,
  responseType: 'arraybuffer' | 'blob' | 'text',
): Promise<T> {
  const { data } = await mailApi.get(`/attachments/${attachmentId}/preview-file`, { responseType });
  return data as T;
}

// ── Recipient Classification ───────────────────────────────────────────────

export interface RecipientClassificationResult {
  internalRecipients: string[];
  externalRecipients: string[];
  allInternal: boolean;
}

export async function classifyRecipientsByRealm(
  mailboxId: string,
  recipients: string[],
): Promise<RecipientClassificationResult> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/recipients/classify`, { recipients });
  return data;
}

// ── Aliases ─────────────────────────────────────────────────────────────────

export async function fetchAliases(mailboxId: string): Promise<{ id: string; aliasEmail: string }[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/aliases`);
  const rows: Array<Record<string, unknown>> = Array.isArray(data?.aliases)
    ? data.aliases
    : Array.isArray(data)
      ? data
      : [];
  return rows.map((r) => ({
    id: String(r.alias_id ?? r.id ?? ''),
    aliasEmail: String(r.source ?? r.alias_email ?? r.aliasEmail ?? ''),
  }));
}

export async function createAlias(mailboxId: string, aliasEmail: string): Promise<void> {
  // Backend takes split local_part + domain fields, not a raw email.
  const at = aliasEmail.lastIndexOf('@');
  if (at <= 0 || at === aliasEmail.length - 1) {
    throw new Error('Enter a valid email address');
  }
  const local_part = aliasEmail.slice(0, at);
  const domain = aliasEmail.slice(at + 1);
  await mailApi.post(`/mailboxes/${mailboxId}/aliases`, { local_part, domain });
}

export async function deleteAlias(mailboxId: string, aliasId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/aliases/${aliasId}`);
}

// ── Sync ────────────────────────────────────────────────────────────────────

export async function triggerMailboxSync(mailboxId: string): Promise<{ queued: boolean; reason?: string }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/sync`);
  return data;
}

// ── Matters (EdgeLex DMS) ───────────────────────────────────────────────────

export interface MatterSearchResult {
  id: string;
  matterNumber: string;
  matterName: string;
}

export async function searchMatters(query: string): Promise<MatterSearchResult[]> {
  const { data } = await mailApi.get('/matters/search', { params: { q: query } });
  return data;
}

// ── Report / Snooze ─────────────────────────────────────────────────────────

export type ReportReason = 'spam' | 'phishing' | 'abuse';

export async function reportMessage(
  mailboxId: string,
  messageId: string,
  reason: ReportReason,
  blockSender: boolean = false,
): Promise<{ success: boolean; movedToFolderId: string | null; blocked: boolean; blockedSenderId?: string | null }> {
  const { data } = await mailApi.post(
    `/mailboxes/${mailboxId}/messages/${messageId}/report`,
    { reason, block_sender: blockSender },
  );
  return data;
}

export interface BlockedSender {
  id: string;
  mailboxId: string;
  mailboxEmail?: string | null;
  senderAddress?: string | null;
  senderDomain?: string | null;
  reason: string;
  source: string;
  isActive: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export async function listBlockedSenders(mailboxId: string): Promise<BlockedSender[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/blocked-senders`);
  return data.blockedSenders || data.blocked_senders || [];
}

export async function createBlockedSender(
  mailboxId: string,
  input: { senderAddress?: string; senderDomain?: string; reason?: string },
): Promise<BlockedSender> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/blocked-senders`, {
    senderAddress: input.senderAddress,
    senderDomain: input.senderDomain,
    reason: input.reason,
  });
  return data.blockedSender || data.blocked_sender;
}

export async function deleteBlockedSender(mailboxId: string, blockedSenderId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/blocked-senders/${blockedSenderId}`);
}

export async function snoozeMessage(
  mailboxId: string,
  messageId: string,
  until: Date,
): Promise<{ success: boolean; snoozedFolderId: string | null }> {
  const { data } = await mailApi.post(
    `/mailboxes/${mailboxId}/messages/${messageId}/snooze`,
    { until: until.toISOString() },
  );
  return data;
}

export async function unsnoozeMessage(
  mailboxId: string,
  messageId: string,
): Promise<{ success: boolean; restoredToFolderId: string | null }> {
  const { data } = await mailApi.delete(
    `/mailboxes/${mailboxId}/messages/${messageId}/snooze`,
  );
  return data;
}

// ── App passwords list / signature AI draft ────────────────────────────────

export interface AppPasswordSummary {
  id: string;
  name: string;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  expiresAt: string | null;
  isActive: boolean;
  createdAt: string;
}

function mapAppPasswordSummary(row: Record<string, unknown>): AppPasswordSummary {
  return {
    id: String(row.appPasswordId ?? row.app_password_id ?? row.id ?? ''),
    name: String(row.name ?? 'App password'),
    lastUsedAt: (row.lastUsedAt ?? row.last_used_at ?? null) as string | null,
    lastUsedIp: (row.lastUsedIp ?? row.last_used_ip ?? null) as string | null,
    expiresAt: (row.expiresAt ?? row.expires_at ?? null) as string | null,
    isActive: Boolean(row.isActive ?? row.is_active ?? true),
    createdAt: String(row.createdAt ?? row.created_at ?? ''),
  };
}

export async function listAppPasswords(mailboxId: string): Promise<AppPasswordSummary[]> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/app-passwords`);
  const rows: Array<Record<string, unknown>> = Array.isArray(data?.appPasswords)
    ? data.appPasswords
    : Array.isArray(data?.app_passwords)
      ? data.app_passwords
      : Array.isArray(data)
        ? data
        : [];
  return rows.map(mapAppPasswordSummary).filter((row) => row.id);
}

export async function generateAppPassword(
  mailboxId: string,
  name: string,
  protocols: string[],
): Promise<{ password: string; appPassword?: AppPasswordSummary }> {
  const { data } = await mailApi.post(`/mailboxes/${mailboxId}/app-passwords`, {
    name,
    protocols,
  });
  const appPasswordRow = data?.appPassword ?? data?.app_password;
  return {
    password: String(data?.password ?? ''),
    appPassword: appPasswordRow ? mapAppPasswordSummary(appPasswordRow) : undefined,
  };
}

export async function revokeAppPassword(mailboxId: string, passwordId: string): Promise<void> {
  await mailApi.delete(`/mailboxes/${mailboxId}/app-passwords/${passwordId}`);
}

export interface SignatureDraft {
  label: string;
  html: string;
  plain: string;
}

export interface SignatureDraftResult {
  variants: SignatureDraft[];
  providerUsed: 'local' | 'frontier';
}

export interface MailboxSignature {
  html: string;
  plain: string;
  position: 'top' | 'bottom' | null;
}

export async function fetchMailboxSignature(mailboxId: string): Promise<MailboxSignature> {
  const { data } = await mailApi.get(`/mailboxes/${mailboxId}/settings`);
  const mb = data?.settings ?? data ?? {};
  return {
    html: (mb.signatureHtml as string) ?? (mb.signature_html as string) ?? '',
    plain: (mb.signaturePlain as string) ?? (mb.signature_plain as string) ?? '',
    position: (mb.signaturePosition as 'top' | 'bottom' | null) ?? (mb.signature_position as 'top' | 'bottom' | null) ?? null,
  };
}

export async function saveMailboxSignature(
  mailboxId: string,
  sig: Partial<MailboxSignature>,
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (sig.html !== undefined) payload.signatureHtml = sig.html;
  if (sig.plain !== undefined) payload.signaturePlain = sig.plain;
  await mailApi.patch(`/mailboxes/${mailboxId}/settings`, payload);
}

export async function saveSendIdentitySignature(
  mailboxId: string,
  identityId: string,
  sig: Partial<MailboxSignature>,
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (sig.html !== undefined) payload.signatureHtml = sig.html;
  if (sig.plain !== undefined) payload.signaturePlain = sig.plain;
  if (sig.position !== undefined) payload.signaturePosition = sig.position;
  await mailApi.patch(`/mailboxes/${mailboxId}/send-identities/${identityId}/signature`, payload);
}

export async function draftSignatureWithAI(
  mailboxId: string,
  input: {
    title?: string;
    firm?: string;
    phone?: string;
    notes?: string;
    variantCount?: number;
    preferred?: 'local' | 'frontier';
  },
): Promise<SignatureDraftResult> {
  void mailboxId;
  const label = input.title ? `${input.title} Signature` : 'Standard Signature';
  const name = input.title || '';
  const firm = input.firm || 'WeBelieve';
  const phone = input.phone ? `<br />${input.phone}` : '';
  return {
    providerUsed: 'local',
    variants: [{
      label,
      html: `<div>${name ? `${name}<br />` : ''}${firm}${phone}</div>`,
      plain: [name, firm, input.phone].filter(Boolean).join('\n'),
    }],
  };
}

// ── Local Directory Picker ──────────────────────────────────────────────────
import type {
  ContactDirectoryEntry,
  ContactSearchResult,
  ContactSearchScope,
} from '../types/contact.types';

export type {
  ContactDirectoryEntry,
  ContactSearchResult,
  ContactSearchScope,
} from '../types/contact.types';

export interface SearchContactsArgs {
  q: string;
  scope?: ContactSearchScope;
  mailboxId?: string;
  matterId?: string;
  limit?: number;
}

export async function searchContacts(args: SearchContactsArgs): Promise<ContactSearchResult> {
  const params: Record<string, string | number | undefined> = {
    q: args.q,
    scope: args.scope,
    mailboxId: args.mailboxId,
    matterId: args.matterId,
    limit: args.limit,
  };
  const { data } = await mailApi.get('/directory/search', { params });
  return data as ContactSearchResult;
}

export async function fetchRecentRecipients(
  mailboxId: string,
  limit = 25,
): Promise<ContactDirectoryEntry[]> {
  const { data } = await mailApi.get('/directory/recent', {
    params: { mailboxId, limit },
  });
  return (data?.contacts ?? []) as ContactDirectoryEntry[];
}

export async function fetchMatterParticipants(matterId: string): Promise<ContactDirectoryEntry[]> {
  void matterId;
  return [];
}

// ── Recipient Intelligence (Phase 4) ────────────────────────────────────────

export type SuggestionReason = 'same_matter' | 'cooccurred';
export type ConflictSeverity = 'block' | 'flag' | 'note';

export interface RecipientSuggestion {
  contact: ContactDirectoryEntry;
  reason: SuggestionReason;
  reasonText: string;
  score: number;
}

export interface RecipientConflict {
  recipientEmail: string;
  severity: ConflictSeverity;
  reason: string;
  matterIds?: string[];
}

export interface IntelligenceCommonInput {
  mailboxId: string;
  currentMatterId?: string;
  recipients: Array<{ email: string; name?: string }>;
}

export async function fetchRecipientSuggestions(
  input: IntelligenceCommonInput,
): Promise<RecipientSuggestion[]> {
  void input;
  return [];
}

export async function fetchRecipientConflicts(
  input: IntelligenceCommonInput & { isPrivileged?: boolean },
): Promise<RecipientConflict[]> {
  void input;
  return [];
}

// ── Moderation Queue (Phase 5) ──────────────────────────────────────────────

export interface ModerationQueueRow {
  queueId: string;
  mailboxId: string;
  senderEmail: string;
  subject: string;
  to: Array<{ email: string; name?: string }>;
  cc: Array<{ email: string; name?: string }>;
  bcc: Array<{ email: string; name?: string }>;
  groups: Array<{
    groupId: string;
    groupEmail: string;
    displayName: string;
    moderators: string[];
  }>;
  createdAt: string;
  canModerate: boolean;
  isOwnSend: boolean;
}

export async function fetchModerationQueue(mailboxId: string): Promise<ModerationQueueRow[]> {
  const { data } = await mailApi.get('/moderation/queue', { params: { mailboxId } });
  return (data?.rows ?? []) as ModerationQueueRow[];
}

export async function approveModerationQueue(args: {
  queueId: string;
  mailboxId: string;
}): Promise<void> {
  await mailApi.post(`/moderation/queue/${args.queueId}/approve`, { mailboxId: args.mailboxId });
}

export async function rejectModerationQueue(args: {
  queueId: string;
  mailboxId: string;
  reason?: string;
}): Promise<void> {
  await mailApi.post(`/moderation/queue/${args.queueId}/reject`, {
    mailboxId: args.mailboxId,
    reason: args.reason,
  });
}

export default mailApi;
